// src/utils/timeUtils.js
/* ========================
 * formatRelativeTime - Optimized relative time for high-use chat feeds
 * Uses current local time display with smart relative formatting
 * ======================== */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "Just now";
  
  const now = new Date();
  const messageTime = timestamp instanceof Date ? timestamp : 
                     (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
  
  const diffMs = now.getTime() - messageTime.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  // For very recent messages (< 1 minute), always show "Just now"
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  
  // For older messages, show actual date
  return messageTime.toLocaleDateString();
};

/* ========================
 * shouldShowTimestampBetween - Ultra-conservative timestamp headers for smooth UX
 * Only shows headers for first message and 60+ minute gaps
 * ======================== */
export const shouldShowTimestampBetween = (currentTimestamp, previousTimestamp) => {
  if (!currentTimestamp || !previousTimestamp) return true;
  
  const current = currentTimestamp instanceof Date ? currentTimestamp : 
                 (currentTimestamp.toDate ? currentTimestamp.toDate() : new Date(currentTimestamp));
  const previous = previousTimestamp instanceof Date ? previousTimestamp : 
                  (previousTimestamp.toDate ? previousTimestamp.toDate() : new Date(previousTimestamp));
  
  const diffMs = current.getTime() - previous.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  // Only show new timestamp header if more than 60 minutes apart
  // This creates a very clean, minimal chat experience
  return diffMinutes > 60;
};

/* ========================
 * parseFirestoreTimestamp - Safely converts various timestamp formats
 * Handles Firestore timestamps, Date objects, and ISO strings
 * ======================== */
export const parseFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  return new Date();
};

/* ========================
 * isValidTimestamp - Validates timestamp objects for UI display
 * Ensures timestamps are valid before attempting to format them
 * ======================== */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  
  try {
    const date = parseFirestoreTimestamp(timestamp);
    return date instanceof Date && !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

/* ========================
 * isFirestoreTimestamp - Detects Firestore timestamp objects
 * Checks for the specific {seconds, nanoseconds} structure
 * ======================== */
export const isFirestoreTimestamp = (timestamp) => {
    return timestamp && typeof timestamp === 'object' && 
           'seconds' in timestamp && 'nanoseconds' in timestamp;
};

/* ========================
 * convertToDate - Enhanced version of parseFirestoreTimestamp
 * Handles the specific Firestore {seconds, nanoseconds} format
 * ======================== */
export const convertToDate = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle Firestore Timestamp with seconds/nanoseconds
    if (isFirestoreTimestamp(timestamp)) {
        return new Date(timestamp.seconds * 1000);
    }
    
    // Handle numeric timestamp
    if (typeof timestamp === 'number') {
        return new Date(timestamp);
    }
    
    // Handle Date object
    if (timestamp instanceof Date) {
        return timestamp;
    }
    
    // Handle string timestamp
    try {
        const date = new Date(String(timestamp));
        return isValidTimestamp(date) ? date : null;
    } catch {
        return null;
    }
};

/* ========================
 * formatDate - Uses date-fns style formatting for consistency with ExpandedHistoryPanel
 * ======================== */
export const formatDate = (timestamp) => {
    try {
        const date = convertToDate(timestamp);
        if (!date || !isValidTimestamp(date)) return 'Recently';
        return formatRelativeTime(date);
    } catch (error) {
        console.warn('Date formatting error:', error);
        return 'Recently';
    }
};

/* ========================
 * formatDateForSidebar - Specific formatting for sidebar history items
 * ======================== */
export const formatDateForSidebar = (timestamp) => {
    if (!timestamp) return '';
    const date = convertToDate(timestamp);
    if (!date || !isValidTimestamp(date)) {
        return '';
    }
    try {
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        if (isToday) {
            return formatRelativeTime(date);
        } else {
            return date.toLocaleDateString('en-US', { 
                weekday: 'short', 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            }).toUpperCase();
        }
    } catch (error) {
        console.warn('Error formatting date for sidebar:', error);
        return '';
    }
};